import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";

import { Link } from "@/components/ui/link/link";
import { cn } from "@/lib/utils";

export const SIGNUP_HREF = "https://app.wayflyer.com/authn/signup";

/** The signature 4-stop brand gradient used for "Ask anything." and the quote. */
export const SIGNATURE_GRADIENT =
  "linear-gradient(90deg, #fe634d 3%, #e842a3 35%, #b033e0 70%, #5e1fff 100%)";

/** The hero's purple-pink word gradient — deliberately different from the signature gradient. */
export const HERO_WORD_GRADIENT =
  "linear-gradient(90deg, #8a2be8 0%, #b033e0 45%, #e83fc8 100%)";

interface SectionProps {
  children: ReactNode;
  className?: string;
  id?: string;
}

/** Full-width section shell: shared horizontal padding, page background. */
export function Section({ children, className, id }: SectionProps) {
  return (
    <section
      id={id}
      className={cn("bg-[#fafaf7] px-5 py-16 md:px-10 lg:px-20", className)}
    >
      {children}
    </section>
  );
}

interface GradientTextProps {
  children: ReactNode;
  gradient?: string;
  className?: string;
}

/** Inline span with a text-clipped gradient fill. */
export function GradientText({
  children,
  gradient = SIGNATURE_GRADIENT,
  className,
}: GradientTextProps) {
  return (
    <span
      className={cn("bg-clip-text text-transparent", className)}
      style={{ backgroundImage: gradient }}
    >
      {children}
    </span>
  );
}

interface PillCtaProps {
  href: string;
  children: ReactNode;
  className?: string;
  /** White pill / blue circle, for use on dark backgrounds. */
  inverse?: boolean;
  external?: boolean;
  id?: string;
}

/** Rounded pill CTA: label + trailing circular arrow chip. Used in the hero, the
 * timeline's closing CTA slide, and the footer banner (inverse). */
export function PillCta({
  href,
  children,
  className,
  inverse = false,
  external = true,
  id,
}: PillCtaProps) {
  return (
    <Link
      id={id}
      href={href}
      external={external}
      // fontSize is inline, not a Tailwind class: this project's cn() merge
      // config only recognises its custom named font-size tokens, so an
      // arbitrary text-[14px] utility here gets misclassified into the same
      // group as the text-color utility below and silently dropped.
      style={{ fontSize: "14px" }}
      className={cn(
        "inline-flex h-10 items-center gap-3 rounded-full py-1.5 pr-1.5 pl-4 font-medium tracking-[-0.02em] no-underline transition-opacity hover:opacity-90",
        inverse ? "bg-white text-[#0d0d0d]" : "bg-[#001fff] text-white",
        className
      )}
    >
      {children}
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-full",
          inverse ? "bg-[#001fff]" : "bg-white/20"
        )}
      >
        <ArrowRight
          className="size-4 text-white"
          strokeWidth={1.4}
          aria-hidden="true"
        />
      </span>
    </Link>
  );
}
